import SkeletonCell from '../../../../../../../common/_custom/SkeletonCell/SkeletonCell';
import Button from '../../../../../../../common/_custom/Button/Button';
import RedirectIcon from '../../../../../../../assets/Svg/RedirectIcon';
import styles from './WebsitePreview.module.scss';
import { WebsitePreviewState } from './useWebsitePreview';

// Figma "View Website button" iteration 1.0: open_in_new icon is 12px, purple.
const REDIRECT_ICON_SIZE = '12';

interface WebsitePreviewProps {
    /** Company name, for the initial-letter last resort. */
    companyName?: string;
    preview: WebsitePreviewState;
    /** Opens the website in a new tab. Omit to render the strip without the action. */
    onOpenWebsite?: () => void;
}

/**
 * Preview strip under the Website field showing what the company's site looks like.
 *
 * Renders, in order of preference:
 *   1. a wide image of the site — either its declared og:image or a live
 *      screenshot of the homepage, whichever the server resolved
 *   2. the company logo or favicon, centred at its own size
 *   3. nothing — when neither exists (dead/unknown domain), no strip renders;
 *      a placeholder would read as a confirmed site
 *
 * The fallback-chain state lives in useWebsitePreview; this component only
 * renders whatever the hook resolved and reports <img> failures back to it.
 *
 * A redirect badge sits over every ready state: icon-only in the top-right
 * corner, expanding in place into a "View Website" pill on hover (per the
 * Figma "View Website button" section, iteration 1.0) — so a valid website is
 * always openable, screenshot or not. Only the dark scrim is hero-image-only:
 * over a small centred logo on a light background it reads as an error, not a
 * treatment.
 *
 * Nothing renders until there is a valid website, so the modal reserves no
 * empty space up front.
 */
const WebsitePreview: React.FC<WebsitePreviewProps> = ({ companyName, preview, onOpenWebsite }) => {
    const { status, imageUrl, heroFailed, onHeroError, notFound, logoSource, onLogoError, onLogoLoad } = preview;

    if (status === 'idle') return null;

    if (status === 'loading') {
        return (
            <div className={styles.WebsitePreview}>
                <SkeletonCell className={styles.WebsitePreviewSkeleton} data-testid="website-preview-loading" />
            </div>
        );
    }

    // The redirect badge renders over every ready state — hero image OR logo/initial
    // fallback — so a valid website is always openable. Only the dark scrim is
    // hero-only (over a small logo on a light background it reads as an error).
    const redirectBadge = onOpenWebsite ? (
        <Button
            type="button"
            className={styles.WebsitePreviewRedirect}
            onClick={onOpenWebsite}
            aria-label="View website in a new tab"
            text={
                <>
                    <RedirectIcon width={REDIRECT_ICON_SIZE} height={REDIRECT_ICON_SIZE} color="var(--primary-purple)" />
                    {/* Expands in place on hover — see .WebsitePreviewRedirectLabel */}
                    <span className={styles.WebsitePreviewRedirectLabel}>View Website</span>
                </>
            }
        />
    ) : null;

    if (imageUrl && !heroFailed) {
        return (
            <div className={styles.WebsitePreview}>
                <img
                    src={imageUrl}
                    alt={companyName ? `${companyName} website preview` : 'Website preview'}
                    className={styles.WebsitePreviewHero}
                    data-testid="website-preview-hero"
                    onError={onHeroError}
                />
                <span className={styles.WebsitePreviewScrim} aria-hidden="true" />
                {redirectBadge}
            </div>
        );
    }

    // The site produced nothing real, so no strip renders — the "couldn't find
    // this website" feedback lives on the Website input's own error treatment
    // (see AddToWatchlistV2, reading the same hook flag).
    if (notFound || !logoSource) return null;

    return (
        <div className={styles.WebsitePreview}>
            <img
                src={logoSource}
                alt={companyName ? `${companyName} logo` : 'Company logo'}
                className={styles.WebsitePreviewLogo}
                data-testid="website-preview-logo"
                onError={onLogoError}
                onLoad={onLogoLoad}
            />
            {redirectBadge}
        </div>
    );
};

export default WebsitePreview;
